import type { Identifiable } from '~/models/identifiable'
import { get } from 'lodash'
/**
 * Path: Path to the property you want to update
 * Example: ['Stock.Amount'] or ['Amount']
 */
type Paths<T extends object> = {
  [K in keyof T]: T[K] extends object ? [K, ...Paths<T[K]>] : [K]
}[keyof T]

export abstract class LocalStateChange<T extends Identifiable> {
  constructor(
    protected _value: T,
    private _depth: number = 0,
  ) {}

  get depth(): number {
    return this._depth
  }

  isSource(source: T): boolean {
    if (this._depth === 0 || this._depth === undefined) {
      return source.id === this.id
    }

    return (
      source.ref[Array(this._depth).fill('parent').join('.')]?.id === this.id
    )
  }

  get id(): string {
    if (this._depth === 0) {
      return this._value.id
    }

    return (
      this.getParentAtDepth(this._value.ref, this._depth)?.id || this._value.id
    )
  }

  getParentAtDepth(obj: any, depth: number): any {
    let current = obj
    for (let i = 0; i < depth; i++) {
      current = current?.parent
      if (!current) break
    }
    return current
  }

  isDifferent(source: T): boolean {
    return source.id !== this._value.id
  }
}

export class LocalStateUpdate<
  T extends Identifiable,
> extends LocalStateChange<T> {
  private path: Paths<T>

  /**
   * Temporarily update local state. This local state is cleared when firestore data is updated.
   *
   * @param value The value of the identifiable object
   * @param path The path to the property you want to update
   * @param depth 0 = current object, 2 = parent
   *
   * TODO: Rename depth to isSubcollection
   */
  constructor(value: T, path: Paths<T>, depth?: number) {
    super(value, depth)
    this.path = path
  }

  get value(): T {
    return this._value as T
  }

  override isDifferent(source: T): boolean {
    return (
      get(source, this.path.join('.')) !== get(this._value, this.path.join('.'))
    )
  }
}

export class LocalStateAdd<T extends Identifiable> extends LocalStateChange<T> {
  getValue(): T {
    return this._value
  }
}

export class LocalStateRemove<
  T extends Identifiable,
> extends LocalStateChange<T> {}
